import { FeatureFlags, useFeatureFlag } from '@/providers/FeatureFlagContext'
import { useMutation, useQuery } from '@api/hooks'
import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { Drawer } from '@mui/material'
import ActionCenterTasksApi, { ActionCenterKeys } from '@services/actionCenterTasks.api'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { CreateMarketBasketExcelExportReq } from '@services/marketBaskets.types'
import { isLowerEnvironment } from '@utils/EnvironmentHelper'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import { excelExportSchema } from './BasketExport.schema'
import { excelExportDefaults } from './BasketExport.utils'
import ExcelExportForm from './ExcelExportForm'

type Props = {
	marketBasketId: Id
	open: boolean
	onClose: () => void
}

type Schema = z.infer<typeof excelExportSchema>

/**
 * Displays a drawer to export an excel basket report.
 */
export default function ExcelExport({ open, marketBasketId, onClose }: Props) {
	const actionCenterEnabled = useFeatureFlag(FeatureFlags.ActionCenter)
	const contractLoadingEnabled = useBoolVariation('feature-art-1113-contract-loading-task', false)

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketsKeys.marketBasket(marketBasketId),
		queryFn: () => MarketBasketsApi.marketBasket(marketBasketId as string),
		enabled: open,
	})

	const { data: tasks, isLoading: tasksLoading } = useQuery({
		queryKey: ActionCenterKeys.tasks(marketBasketId),
		queryFn: () => ActionCenterTasksApi.tasks(marketBasketId),
		enabled: open && actionCenterEnabled,
	})

	const filteredTasks = (tasks || []).filter(
		(task) => contractLoadingEnabled || task.task !== 'CONTRACT_LOADING'
	)

	const { mutate } = useMutation({
		mutationFn: ({ id, settings }: CreateMarketBasketExcelExportReq) =>
			MarketBasketsApi.export({ id, settings }),

		onSuccess: (data) => {
			window.open(
				`/app/market-baskets/${data.marketBasketId}/exports/${data.id}`,
				'_blank',
				'noreferrer'
			)
		},
	})

	const handleSubmit = (values: Schema) => {
		const settings = {
			...values,
			includeAllPurchases: values.purchasesToInclude !== 'IN_BASKET',
		}

		mutate({ id: marketBasketId, settings } as CreateMarketBasketExcelExportReq)
		onClose()
	}

	const methods = useForm<Schema>({
		mode: 'onBlur',
		resolver: zodResolver(excelExportSchema),
		shouldFocusError: true,
		defaultValues: excelExportDefaults,
	})

	return (
		<Drawer open={open} variant="temporary" anchor="right" onClose={onClose}>
			<FormProvider {...methods}>
				<form id="basket-settings" onSubmit={methods.handleSubmit(handleSubmit)}>
					<ExcelExportForm
						loading={isLoading}
						marketBasket={data}
						onClose={onClose}
						tasks={actionCenterEnabled ? filteredTasks : []}
						tasksLoading={tasksLoading}
					/>
				</form>
				{isLowerEnvironment && <DevTool control={methods.control} placement="top-right" />}
			</FormProvider>
		</Drawer>
	)
}
